import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { LocalSysChartAcctMapDeleteService } from './local-sys-chart-acct-map-delete.service';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-local-sys-chart-acct-map-delete',
    templateUrl: './local-sys-chart-acct-map-delete.component.html',
    styleUrls: ['./local-sys-chart-acct-map-delete.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LocalSysChartAcctMapDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  filterParams: any = {};
  rowData: any = {};
  gridDataInput: any = {};

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Delete this Mapping?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following fir163jsync pattern
  globalParams: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = false;
  gridData: any[] = [];
  transFlag: boolean = false;
  transFlagLoading: boolean = false;
  transDetails: any[] = [];

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private localSysChartAcctMapDeleteService: LocalSysChartAcctMapDeleteService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.gridDataInput = gridPayloadFromActiveScreen(activeScreen) || activeScreen?.data?.selected || {};
  }

  init(): void {
    // Following fir163jsync pattern - check if all required data is available
    if (this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.gridDataInput && Object.keys(this.gridDataInput).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Process grid data if gridDataInput or rowData is available
    if (this.gridDataInput || this.rowData) {
      this.processGridData();
    }
  }

  processGridData(): void {
    // Process grid data exactly like AngularJS controller
    // Use gridDataInput if available, otherwise fall back to rowData
    const sourceData = this.gridDataInput && Object.keys(this.gridDataInput).length > 0 
      ? this.gridDataInput 
      : this.rowData;
    
    if (sourceData != null) {
      this.gridData = [];
      
      // Clone the grid data like AngularJS
      const tempGridData = _.clone(sourceData);
      const gridDataMultipleSelect = _.clone(tempGridData);
      
      if (gridDataMultipleSelect != undefined) {
        // Handle both array and object formats
        if (Array.isArray(gridDataMultipleSelect)) {
          this.gridData = gridDataMultipleSelect.map(item => {
            const dataItem = item.data || item;
            return {
              data: dataItem
            };
          });
        } else if (typeof gridDataMultipleSelect === 'object') {
          // Handle object format like AngularJS: vm.gridDataMultipleSelect[key].data
          for (var key in gridDataMultipleSelect) {
            const dataItem = gridDataMultipleSelect[key].data || gridDataMultipleSelect[key];
            this.gridData.push({
              data: dataItem
            });
          }
        }
      }
    }
  }

  onConfirm(): void {
    this.confirmDelete();
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.showModal = false;
    
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-local-sys-chart-acct-map-delete-ce')) {
      document.querySelector('gtw-local-sys-chart-acct-map-delete-ce')?.dispatchEvent(updateEvent);
    }
  }
  
  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmDelete(): void {
    // Follow exact AngularJS behavior - call trans_available first
    this.transAvailable();
  }

  transAvailable(): void {
    // Build local_acct_send exactly like AngularJS
    let localAcctSend = '';
    if (this.gridData && this.gridData.length > 0) {
      localAcctSend = this.gridData[0].data?.LOCAL_ACCT_KEY || '';
      
      if (this.gridData.length > 1) {
        for (let s = 1; s < this.gridData.length; s++) {
          localAcctSend += '_' + (this.gridData[s].data?.LOCAL_ACCT_KEY || '');
        }
      }
    }

    // Build params exactly like AngularJS
    const params = {
      "action_code": 'sqdwvm',
      "tax_year": this.filterParams?.tax_year || this.globalParams?.tax_year || '',
      "scenario": this.filterParams?.scenario || this.globalParams?.scenario || '',
      "jcd_key": this.filterParams?.jcd_key || this.globalParams?.jcd_key || '',
      "local_acct_key": localAcctSend,
      "chart_key": this.gridData && this.gridData.length > 0 ? this.gridData[0].data?.LOCAL_CHART_KEY : ''
    };

    // Call service to check transactions
    this.localSysChartAcctMapDeleteService.checkTransactions(
      this.GENERAL_CONFIG.base_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          // Show error message
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          this.transFlag = false;
          this.transFlagLoading = true;
        } else {
          this.transDetails = data.jsonObject || [];
          
          if (this.transDetails.length == 0) {
            // No transactions - proceed with delete
            this.proceedWithDelete();
          } else {
            // Transactions available - show message and close modal
            this.cancel();
            this.gtwSnackbarService.openSnackBar(
              "Transactions are available with this account , Please Delete Transactions First",
              'gtw-snackbar--yellow'
            );
          }
        }
      },
      (error: any) => {
        console.error('Transaction check error:', error);
        this.gtwSnackbarService.openSnackBar('Error checking transactions. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  proceedWithDelete(): void {
    // Build delete details exactly like AngularJS
    const tempConfirmObj: any = {};
    const deleteDetails: any[] = [];

    for (let e = 0; e < this.gridData.length; e++) {
      const tempObj: any = {};
      // 🚀 CRITICAL: Numeric fields should be numbers (not strings)
      tempObj['system_acct_key'] = parseInt(this.gridData[e].data?.SYS_ACCT_KEY) || 0;
      tempObj['local_acct_key'] = parseInt(this.gridData[e].data?.LOCAL_ACCT_KEY) || 0;
      tempObj['local_chart_key'] = parseInt(this.gridData[e].data?.LOCAL_CHART_KEY) || 0;
      // String fields remain as strings
      tempObj['tax_year'] = String(this.filterParams?.tax_year || this.globalParams?.tax_year || '');
      tempObj['scenario'] = String(this.filterParams?.scenario || this.globalParams?.scenario || '');
      tempObj['jcd_key'] = String(this.filterParams?.jcd_key || this.globalParams?.jcd_key || '');

      deleteDetails.push(tempObj);
    }

    // Use first delete detail as confirm_obj (like AngularJS)
    const confirmObj = deleteDetails.length > 0 ? deleteDetails[0] : {};
    const message = "Data has been successfully Deleted";

    // Call sendDeleteDetails function like AngularJS
    this.sendDeleteDetails(confirmObj, deleteDetails, message);
  }

  sendDeleteDetails(confirmObj: any, deleteDetails: any[], message: string): void {
    // Follow exact AngularJS sendDeleteDetails function behavior
    // Show loader (equivalent to $rootScope.$broadcast("showLoader", true))
    this.modalConfig = { ...this.modalConfig, loading: true };
    
    // Call deleteChartAcctMap service method
    this.localSysChartAcctMapDeleteService.deleteChartAcctMap(
      this.GENERAL_CONFIG.base_url, // Use base_url
      confirmObj, // Pass confirm_obj
      deleteDetails, // Pass deleteDetails array
        { globalParams: this.globalParams },
      this.filterParams, // Pass filterParams
      this.rowData // Pass rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show success message (equivalent to AlertService.add("success", message, 4000))
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        }
      },
      (error: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        console.error('Delete error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
      }
    );
  }
}
